import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useRef, useState, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';

import { StateContext } from '@/context/index';

import { getPageNumber, isWHEnv } from '@/utils/helper';
import cn from 'classnames';

import Text from '@/atoms/Text/Text';
import Icon from '@/atoms/Icon/Icon';
import Button from '@/atoms/Button/Button';
import Checkbox from '@/atoms/Checkbox/Checkbox';

function FilterDropdown({ getApplyAnalytics, name, slug, tags, queryTagsKey }) {
  const router = useRouter();
  const dropdownNode = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [checkedState, setCheckedState] = useState(
    new Array(tags.length).fill(false)
  );
  const {
    dynamicGridParam: { [queryTagsKey]: ctxDynamicGridParam },
  } = useContext(StateContext);

  //add event listener to close dropdown when clicking outside of dropdown's node
  useEffect(() => {
    const handleHideDropdown = (e) => {
      //if clicked outside of dropdown then close dropdown
      if (
        !dropdownNode.current.contains(e.target) ||
        e.target.classList.contains('hide-dropdown')
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleHideDropdown);

    return () => document.removeEventListener('click', handleHideDropdown);
  }, []);

  //on dropdown open tick checkboxes based on context data
  useEffect(() => {
    if (isOpen && ctxDynamicGridParam?.queryTagIds) {
      const newCheckedState = new Array(tags.length).fill(false);
      tags.forEach((i, index) => {
        if (ctxDynamicGridParam.queryTagIds.includes(i.id)) {
          newCheckedState[index] = true;
        }
      });
      setCheckedState(newCheckedState);
    }
  }, [ctxDynamicGridParam?.queryTagIds, isOpen, tags]);

  const toggleDropdown = () => setIsOpen((isOpen) => !isOpen);
  const handleApplyBtnClick = (e) => {
    handleSetUrlParams(true, e);
  };

  const handleClearBtnClick = () => {
    handleSetUrlParams(false);
    setCheckedState(new Array(tags.length).fill(false));
  };

  const handleSetUrlParams = (appendNewTags = false, e = {}) => {
    const url = router.asPath;
    const urlPart = url.split('?');
    let searchParams = '';

    if (urlPart[1]) {
      searchParams = urlPart[1];
    }

    //construct new urlParams object
    const urlParams = new URLSearchParams(searchParams);

    //get params without current tags (clean params)
    let currentParams = urlParams.get(queryTagsKey)
      ? urlParams.get(queryTagsKey).split(',')
      : [];

    const paramsWithoutCurrentTags = currentParams.filter(
      (i) => !tags.some((j) => j.slug === i)
    );

    if (appendNewTags) {
      //append new param based on ticked checkboxes
      tags.forEach((i, index) => {
        if (checkedState[index]) {
          paramsWithoutCurrentTags = [...paramsWithoutCurrentTags, i.slug];
        }
      });
    }

    //update current url search params
    urlParams.set(queryTagsKey, paramsWithoutCurrentTags.join(','));

    //if param have emptu value, delete the param
    if (urlParams.get(queryTagsKey).length === 0) {
      urlParams.delete(queryTagsKey);
    }

    let urlWithoutPageNumber = urlPart[0];
    // Remove pagination on a new filter
    if (getPageNumber(url)) {
      urlWithoutPageNumber = urlWithoutPageNumber.split('/');
      urlWithoutPageNumber.pop();
      urlWithoutPageNumber = urlWithoutPageNumber.join('/');
    }

    //shallow push new url params
    const urlParamsString = decodeURIComponent(urlParams.toString());
    getApplyAnalytics(urlParams.get(queryTagsKey), e);

    router.push(
      `${urlWithoutPageNumber}${
        urlParamsString.length > 0 ? '?' : ''
      }${urlParamsString}`,
      undefined,
      { shallow: true }
    );
  };

  const handleCheckboxChange = (e, index) => {
    const newCheckedState = [...checkedState];
    newCheckedState[index] = e.target.checked;
    setCheckedState(newCheckedState);
  };

  const activeFilterCount =
    ctxDynamicGridParam?.queryTagIds?.filter((q) =>
      tags.some((t) => t.id === q)
    )?.length ?? 0;

  const isWH = isWHEnv();

  return (
    <div
      className="filter-dropdown relative border-b z-20 border-b-dark-warm-grey md:border-b-0 w-full md:w-auto py-[15px] md:py-0"
      ref={dropdownNode}
    >
      <div
        className="flex justify-between md:justify-start items-center cursor-pointer"
        onClick={toggleDropdown}
      >
        <div className="flex items-center">
          <Text tag={isWH ? "body" : "button"} style={`cursor-default dd-kr:text-black dd-en:text-black text-acacia ${isWH ? "uppercase" : ""}`}>
            {name}
          </Text>
          <Text
            style={classNames(
              'bg-acacia rounded-full h-4 text-white leading-4 font-body font-medium text-center text-tiny ml-1',
              {
                'w-4': activeFilterCount === 1,
                'px-1.5': activeFilterCount > 1,
                hidden: activeFilterCount === 0,
              }
            )}
            custom
          >
            {activeFilterCount}
          </Text>
        </div>
        <Icon
          name="chevron-down"
          width={isWH ? 12 : 9}
          height={isWH ? 12 : 9}
          className={`text-acacia dd-kr:ml-[5px] dd-kr:mb-0 dd-en:ml-[5px] dd-en:mb-0 ml-[10px] mb-[2px] transition-transform duration-500 ${
            isOpen && 'rotate-180'
          }`}
        />
      </div>
      <div
        className={`${
          !isOpen && 'hidden'
        } md:absolute md:top-full md:shadow-xl bg-white p-8 md:px-6 md:py-5 mt-3 md:mt-0 md:max-h-[400px] md:overflow-y-auto`}
      >
        <ul className="flex flex-col gap-y-3 mb-6 md:w-max">
          {tags.map((i, index) => (
            <li key={index} className="md:whitespace-nowrap">
              <Checkbox
                id={`chk-filter-${slug}-${index + 1}`}
                value={checkedState[index]}
                checked={checkedState[index]}
                onChange={(e) => handleCheckboxChange(e, index)}
              />
              <label
                htmlFor={`chk-filter-${slug}-${index + 1}`}
                className="font-text font-body text-body ml-2 text-[14px]"
              >
                {i.name}
              </label>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between mb-2 md:mb-0">
          <Button
            style={cn(
              "hide-dropdown text-tag font-small-bold text-grey-6 px-7 py-2 md:p-0 border-1 border-grey-3 md:border-0 rounded-button w-2/4 md:w-auto m-2 md:m-4", {
                "border-none underline underline-offset-[5px] decoration-[1.5px] border-acacia": isWH,
              }
            )}
            onClick={handleClearBtnClick}
          >
            CLEAR
          </Button>
          <Button
            style="hide-dropdown text-tag font-small-bold text-white px-[27.5px] py-[10px] bg-acacia rounded-button w-2/4 md:w-auto m-2 md:m-4 "
            onClick={handleApplyBtnClick}
          >
            APPLY
          </Button>
        </div>
      </div>
    </div>
  );
}

FilterDropdown.propTypes = {
  name: PropTypes.string,
  slug: PropTypes.string,
  tags: PropTypes.array,
  queryTagsKey: PropTypes.string,
};

FilterDropdown.defaultProps = {
  name: '',
  slug: '',
  tags: [],
  queryTagsKey: '',
};

export default FilterDropdown;
